/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type React from "react"
import { useEffect, useState, useRef, useCallback } from "react"
import { Modal, Button, Input, message, Avatar, Spin, Tooltip, Popconfirm } from "antd"
import {
  KeyOutlined,
  CopyOutlined,
  CheckOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from "@ant-design/icons"
import { userService, type TempPasswordData } from "@/services/user/userApi"

interface UserTempPasswordModalProps {
  open: boolean
  user: any | null
  onClose: () => void
}

export const UserTempPasswordModal: React.FC<UserTempPasswordModalProps> = ({ open, user, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [tempData, setTempData] = useState<TempPasswordData | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearCountdown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startCountdown = useCallback(
    (seconds: number) => {
      clearCountdown()
      setRemainingSeconds(seconds)

      if (seconds <= 0) return

      timerRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            clearCountdown()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    },
    [clearCountdown]
  )

  // Check if an active temp password exists on modal open (do not auto-generate)
  useEffect(() => {
    if (!open || !user?.id) {
      setTempData(null)
      setRemainingSeconds(0)
      clearCountdown()
      return
    }

    let isMounted = true
    setLoading(true)

    userService
      .getTempPassword(user.id)
      .then((res) => {
        if (!isMounted) return
        if (res?.data?.temp_password && res.data.expires_in_seconds > 0) {
          setTempData(res.data)
          startCountdown(res.data.expires_in_seconds)
        } else {
          setTempData(null)
          setRemainingSeconds(0)
        }
      })
      .catch((err) => {
        console.error("Failed to check temp password:", err)
        if (isMounted) {
          setTempData(null)
          setRemainingSeconds(0)
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
      clearCountdown()
    }
  }, [open, user?.id, startCountdown, clearCountdown])

  const handleGenerate = async () => {
    if (!user?.id) return
    try {
      setGenerating(true)
      const res = await userService.generateTempPassword(user.id)
      if (res.code === 200 && res.data) {
        setTempData(res.data)
        startCountdown(res.data.expires_in_seconds)
        message.success("Đã tạo mật khẩu tạm thời mới!")
      } else {
        message.error(res.message || "Không thể tạo mật khẩu tạm thời")
      }
    } catch (err: any) {
      message.error(err.message || "Đã xảy ra lỗi khi tạo mật khẩu")
    } finally {
      setGenerating(false)
    }
  }

  const handleRevoke = async () => {
    if (!user?.id) return
    try {
      setRevoking(true)
      const res = await userService.revokeTempPassword(user.id)
      if (res.code === 200) {
        clearCountdown()
        setTempData(null)
        setRemainingSeconds(0)
        message.success("Đã thu hồi mật khẩu tạm thời thành công!")
      } else {
        message.error(res.message || "Thu hồi mật khẩu thất bại")
      }
    } catch (err: any) {
      message.error(err.message || "Đã xảy ra lỗi khi thu hồi mật khẩu")
    } finally {
      setRevoking(false)
    }
  }

  const handleCopy = (text: string, key: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedKey(key)
        message.success(`Đã sao chép ${label}`)
        setTimeout(() => setCopiedKey(null), 2000)
      },
      () => {
        message.error("Không thể sao chép vào bộ nhớ tạm")
      }
    )
  }

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
  }

  if (!user) return null

  const isExpired = !!tempData && remainingSeconds <= 0
  const hasActivePassword = !!tempData && remainingSeconds > 0

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <KeyOutlined className="text-amber-500 text-lg" />
          <span className="font-semibold text-base">Tạo mật khẩu tạm thời (Impersonation)</span>
          <span className="font-normal text-gray-400 text-xs">#{user.id}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div className="flex w-full items-center justify-between">
          <div>
            {hasActivePassword && (
              <Popconfirm
                title="Thu hồi mật khẩu tạm thời?"
                description="Mật khẩu này sẽ bị xóa khỏi Redis và vô hiệu hóa ngay lập tức."
                onConfirm={handleRevoke}
                okText="Thu hồi"
                cancelText="Hủy"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<StopOutlined />} loading={revoking}>
                  Thu hồi mật khẩu
                </Button>
              </Popconfirm>
            )}
          </div>
          <Button onClick={onClose}>Đóng</Button>
        </div>
      }
      width={480}
      destroyOnClose
    >
      <div className="my-2 flex flex-col gap-4">
        {/* User Summary Header */}
        <div className="flex items-center justify-between border-gray-100 border-b pb-3">
          <div className="flex items-center gap-2.5">
            <Avatar
              size={36}
              src={user.avatar}
              icon={<UserOutlined />}
              className="shrink-0 bg-blue-100 text-blue-600"
            />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800 text-sm">{user.name || user.username}</span>
              <span className="text-gray-400 text-xs">
                ID: #{user.id} {user.username ? `• @${user.username}` : ""}
              </span>
            </div>
          </div>

          {hasActivePassword && (
            <Tooltip title="Tạo lại mã mật khẩu tạm mới">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleGenerate}
                loading={generating}
                className="text-xs"
              >
                Tạo mã mới
              </Button>
            </Tooltip>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spin tip="Đang kiểm tra mật khẩu tạm thời..." />
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">
            {/* Hàng 1: Gmail / Email */}
            <div className="flex flex-col gap-1.5">
              <label className="font-medium text-gray-600 text-xs">Gmail / Email đăng nhập:</label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={user.email}
                  prefix={<MailOutlined className="text-gray-400" />}
                  className="bg-gray-50 font-medium font-mono text-sm"
                  size="large"
                />
                <Button
                  size="large"
                  icon={copiedKey === "email" ? <CheckOutlined className="text-emerald-500" /> : <CopyOutlined />}
                  onClick={() => handleCopy(user.email, "email", "email")}
                  className="shrink-0"
                >
                  {copiedKey === "email" ? "Đã chép" : "Sao chép"}
                </Button>
              </div>
            </div>

            {/* Hàng 2: Mật khẩu tạm thời */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-600 text-xs">Mật khẩu tạm thời:</label>
                {hasActivePassword && (
                  <span className="flex items-center gap-1 font-medium text-amber-600 text-xs">
                    <ClockCircleOutlined />
                    <span>Có hiệu lực trong:</span>
                    <strong className="font-mono text-amber-700">{formatCountdown(remainingSeconds)}</strong>
                  </span>
                )}
                {isExpired && <span className="font-medium text-red-500 text-xs">Mật khẩu đã hết hạn</span>}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={tempData?.temp_password || ""}
                  placeholder={isExpired ? "Đã hết hạn - Hãy bấm Tạo mật khẩu" : "Chưa tạo mật khẩu tạm thời"}
                  prefix={<KeyOutlined className="text-amber-500" />}
                  className={
                    hasActivePassword
                      ? "border-blue-200 bg-blue-50/60 font-bold font-mono text-blue-700 text-lg tracking-wider"
                      : "bg-gray-50 font-mono text-gray-400 text-sm"
                  }
                  size="large"
                />
                {hasActivePassword ? (
                  <Button
                    type="primary"
                    size="large"
                    icon={copiedKey === "password" ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={() =>
                      tempData?.temp_password && handleCopy(tempData.temp_password, "password", "mật khẩu tạm thời")
                    }
                    className={
                      copiedKey === "password"
                        ? "shrink-0 bg-emerald-600 hover:bg-emerald-700"
                        : "shrink-0 bg-blue-600 hover:bg-blue-700"
                    }
                  >
                    {copiedKey === "password" ? "Đã chép" : "Sao chép"}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    size="large"
                    icon={<KeyOutlined />}
                    onClick={handleGenerate}
                    loading={generating}
                    className="shrink-0 bg-blue-600 font-medium hover:bg-blue-700"
                  >
                    Tạo mật khẩu
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default UserTempPasswordModal
